import React, { useState, useEffect, useRef } from 'react';
import { X, Database, ScrollText, Bug, Wrench, ChevronUp, ChevronDown, Pencil, Mail, Eye } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { devFilesApi, helpApi } from '../../services/api';
import { activeDbSlug, activeDbName } from '../../services/activeDatabase';
import { useHelp } from '../../context/HelpContext';
import StuiRichText, { StuiRichTextViewer } from '../common/StuiRichText';
import StuiEmailModal from '../common/StuiEmailModal';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';
import { usePreferences, applyThemeToDom } from '../../context/PreferencesContext';
import { useLookups } from '../../context/LookupContext';
import {
  DEBUG_HOVER_TOOLS,
  DEBUG_OVERLAY_TOOLS,
  CSS_VARS_MODE_KEY,
  isDebugPanelVisible,
  syncHoverState,
  syncOverlayTool,
  notifyDebugChanged,
  refreshTabOrder,
} from '../../utils/debugOverlay';
import StuiSelect from '../common/StuiSelect';

// "module:Building" -> "Building", "dataentry:purchasing" -> "purchasing", "dev-plan" -> "Dev Plan"
const defaultHelpTitle = (key) => {
  const bare = key.includes(':') ? key.split(':').pop() : key;
  return bare.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Footer component with the bug reporter, debug tools and per-page help.
 */
const Footer = ({ currentPage, isAdmin }) => {
  const { isSysAdmin, currentUser } = useAuth();
  const location = useLocation();
  const [showAddTask, setShowAddTask] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '', category: 'Medium', entryType: 'Bug' });

  // Permission buckets are keyed by DB slug, and the footer renders on admin and dev pages
  // where there is no database context at all — so a per-database key has to be checked across
  // every bucket here: the question is "can they do this somewhere", and the API still enforces
  // the specific database on the call.
  const anyBucketHas = (key) =>
    Object.values(currentUser?.permissions || {}).some(list => (list || []).includes(key));

  // A globally-granted key lands in the "global" bucket and never under a slug, so it needs
  // the opposite treatment. Do NOT "simplify" these two into anyBucketHas: that is what let
  // an instance-only development.main row switch on Quick Capture everywhere.
  const globalHas = (key) => (currentUser?.permissions?.global || []).includes(key);

  // Who sees the reporter. action.bugreport is GrantScope='Both': granted to PowerUser and
  // AppAdmin by role default, and grantable globally to a reviewer who roams across projects.
  // Either lands in a bucket anyBucketHas reads, which is why it is not globalHas.
  // SysAdmin bypasses permission checks entirely.
  const canReportBug = isSysAdmin || anyBucketHas('action.bugreport');

  // Developers get the same control for ordinary quick capture, so a task or a feature
  // note can still be raised from wherever they happen to be standing. Everyone else
  // files bugs. The API enforces this independently — this only shapes the form.
  const canQuickCapture = isSysAdmin || globalHas('development.main');

  // Options come from AppCfg_LookupValues, the same rows the endpoint validates against,
  // so the picker can never offer a value the API rejects.
  const { getValues } = useLookups();
  const priorityOptions = getValues('Task_Priority');
  const typeOptions = getValues('Task_Type');

  // Debug panel state
  const allTools = [...DEBUG_HOVER_TOOLS, ...DEBUG_OVERLAY_TOOLS];
  const readToolStates = () => {
    const out = {};
    allTools.forEach(t => { out[t.lsKey] = localStorage.getItem(t.lsKey) === 'true'; });
    return out;
  };
  const [toolStates,   setToolStates]   = useState(readToolStates);
  const [debugActive,  setDebugActive]  = useState(isDebugPanelVisible);
  const [debugOpen,    setDebugOpen]    = useState(false);
  const [cssVarsMode,  setCssVarsMode]  = useState(() => localStorage.getItem(CSS_VARS_MODE_KEY) || 'used');
  const [theme,        setTheme]        = useState(() => localStorage.getItem('dawn.uiTheme') || 'standard');
  const debugPanelRef = useRef(null);

  // Restore hover listeners and overlay tools on page load/refresh
  useEffect(() => {
    syncHoverState();
    DEBUG_OVERLAY_TOOLS.forEach(t => {
      if (localStorage.getItem(t.lsKey) === 'true') syncOverlayTool(t.key, true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when tools change from Preferences page
  useEffect(() => {
    const handler = () => {
      setToolStates(readToolStates());
      setDebugActive(isDebugPanelVisible());
      setCssVarsMode(localStorage.getItem(CSS_VARS_MODE_KEY) || 'used');
    };
    window.addEventListener('dawn-debug-changed', handler);
    return () => window.removeEventListener('dawn-debug-changed', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync theme when changed from Preferences page
  useEffect(() => {
    const handler = () => setTheme(localStorage.getItem('dawn.uiTheme') || 'standard');
    window.addEventListener('dawn-theme-changed', handler);
    return () => window.removeEventListener('dawn-theme-changed', handler);
  }, []);

  // Shift+F12 opens/closes panel from any page
  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.key === 'F12') {
        e.preventDefault();
        if (isDebugPanelVisible()) setDebugOpen(o => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Refresh tab order numbers when navigating to a new page
  useEffect(() => {
    const timer = setTimeout(refreshTabOrder, 150);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Close panel on outside click
  useEffect(() => {
    if (!debugOpen) return;
    const handler = (e) => {
      if (debugPanelRef.current && !debugPanelRef.current.contains(e.target)) setDebugOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [debugOpen]);

  // Server-backed preference write path (mirrors + debounced persistence).
  // Quick toggles stay immediate-write — no dirty/save ceremony down here.
  const { setPref, rows: prefRows, slugToDbId, themes, trial, stopTrial, applyTrial } = usePreferences();
  const [applyingTrial, setApplyingTrial] = useState(false);
  // Active themes for everyone; Draft ones only reach (and only show for) SysAdmin.
  const themeChoices = themes.filter(t => t.status === 'Active' || (isSysAdmin && t.status === 'Draft'));

  const handleDebugToggle = (key, lsKey, type, checked) => {
    setPref(`debug.${type}.${key}`, checked ? 'true' : 'false');   // writes mirror + persists
    setToolStates(prev => ({ ...prev, [lsKey]: checked }));
    if (type === 'hover') syncHoverState();
    else                  syncOverlayTool(key, checked);
    notifyDebugChanged();
  };

  const handleCssVarsMode = (value) => {
    setCssVarsMode(value);
    setPref('debug.cssVarsMode', value);
  };

  const handleFooterTheme = (value) => {
    setTheme(value);
    applyThemeToDom(value);
    // Scope rule (Josh 2026-08-11): update the current DB's override when one
    // exists, otherwise write the global row.
    const dbId = slugToDbId[activeDbSlug()];
    const hasDbRow = dbId != null && prefRows.some(r => r.prefKey === 'display.theme' && r.databaseInstanceId === dbId);
    setPref('display.theme', value, hasDbRow ? 'db' : 'global');
    window.dispatchEvent(new Event('dawn-theme-changed'));
  };

  // Get status and notify from notification context
  const { status, notify } = useNotifications();

  // Dynamic version from rev log API — uses the lightweight AllowAnonymous
  // /current-version endpoint so non-SysAdmin footers show the version
  // (a20.046 — was silently blank for AppAdmin+ under real Entra auth).
  const [appVersion, setAppVersion] = useState('');
  const fetchVersion = () => {
    devFilesApi.getCurrentVersion()
      .then(res => setAppVersion(res.data?.currentVersion || ''))
      .catch(() => setAppVersion(''));
  };
  useEffect(() => {
    fetchVersion();
    window.addEventListener('pmd-version-updated', fetchVersion);
    return () => window.removeEventListener('pmd-version-updated', fetchVersion);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Database context (may be null on admin/dev pages)
  const dbContext = useDatabaseSafe();
  const dbDisplayName = dbContext?.dbDisplayName || '';
  // Always know the database name — from context, else this tab's database
  const databaseName = dbContext?.currentDb?.databaseName || dbContext?.currentDb?.id || activeDbName();
  const isPreprod = databaseName.toLowerCase().includes('preprod');
  const badgeLabel = dbDisplayName || 'Admin';

  // ── Help popup (AppCfg_HelpContent via /api/help) ──────────────────────
  // Key: page-registered override (dynamic/data-entry modules) else route slug.
  const { helpOverride } = useHelp();
  const helpKey = helpOverride || currentPage || 'home';
  const [helpRow, setHelpRow] = useState(null);        // { title, contentHtml } | null
  const [helpLoading, setHelpLoading] = useState(false);
  const [helpEditing, setHelpEditing] = useState(false);
  const [helpDraft, setHelpDraft] = useState({ title: '', contentHtml: '' });
  const [helpSaving, setHelpSaving] = useState(false);
  const [showHelpEmail, setShowHelpEmail] = useState(false);

  // Email Support from the help popup: the server locks "DAWN Help (<db>): " on the front of the
  // subject; the page title starts the editable part, so support can triage from the inbox.
  const helpPageTitle = helpRow?.title || defaultHelpTitle(helpKey);
  const helpEmailSubject = helpPageTitle || '';
  const helpEmailContext = {
    Page: helpPageTitle,
    Database: dbDisplayName || databaseName,
    Route: location.pathname,
    'Help Key': helpKey,
  };

  // Edit gate: a GLOBAL action.edithelp grant (SysAdmin bypasses). Help content is
  // application-wide, so the key is GrantScope='Global' and HelpController gates the write
  // with [RequireGlobalPermission] — checking every bucket here would show the pencil to
  // someone the API then refuses.
  const canEditHelp = isSysAdmin || globalHas('action.edithelp');

  const openHelp = async () => {
    setShowHelp(true);
    setHelpEditing(false);
    setHelpLoading(true);
    try {
      const res = await helpApi.get(helpKey);
      setHelpRow(res.data);
    } catch {
      setHelpRow(null); // 404 = nothing written yet
    } finally {
      setHelpLoading(false);
    }
  };

  const startHelpEdit = () => {
    setHelpDraft({
      title: helpRow?.title || defaultHelpTitle(helpKey),
      contentHtml: helpRow?.contentHtml || '',
    });
    setHelpEditing(true);
  };

  const saveHelp = async () => {
    setHelpSaving(true);
    try {
      const res = await helpApi.save(helpKey, helpDraft.title, helpDraft.contentHtml);
      setHelpRow(res.data?.deleted ? null : res.data);
      setHelpEditing(false);
      notify.success('Help saved');
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to save help');
    } finally {
      setHelpSaving(false);
    }
  };

  const getSourcePage = () => {
    // Convert URL path to readable page name
    const page = currentPage || 'home';
    return page.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // What the developer will want to know before they can even reproduce it. Gathering it
  // here is the whole advantage a footer reporter has over somebody describing the
  // problem from memory in a chat message.
  const getReportContext = () => ({
    source: getSourcePage(),
    route: location.pathname,
    database: databaseName || null,
    version: appVersion || null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: navigator.userAgent,
  });

  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;

    try {
      setSaving(true);

      // Status, owner and originator are deliberately absent: the endpoint stamps them
      // from the signed-in identity and the Bug_Triage_Owner lookup. A reporter cannot
      // file a row as somebody else or assign it to a developer.
      await devFilesApi.reportBug({
        task: newTask.task.trim(),
        details: newTask.details.trim(),
        category: newTask.category,
        entryType: canQuickCapture ? newTask.entryType : 'Bug',
        // Read ONLY when the API has no user context, which means auth is disabled —
        // local development. Deployed, the server takes the reporter from the signed-in
        // identity and ignores this, so it is not a spoofing route.
        originator: currentUser?.email?.split('@')[0]?.split('.')[0]?.toLowerCase() || null,
        ...getReportContext(),
      });

      setNewTask({ task: '', details: '', category: 'Medium', entryType: 'Bug' });
      setShowAddTask(false);
      // successAlert, not success: the footer status is a three-second whisper in the
      // corner and a reporter needs to know their report actually landed.
      notify.successAlert('Bug reported. It will be assigned to a developer.');
    } catch (err) {
      notify.error(err.response?.data?.error || err.response?.data?.message || 'Failed to report the bug');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setNewTask({ task: '', details: '', category: 'Medium', entryType: 'Bug' });
    setShowAddTask(true);
  };

  return (
    <>
      <footer className="stui-footer">
        <div className="stui-footer-left">
          <span
            className={`stui-footer-db-badge ${isPreprod ? 'stui-footer-db-badge-preprod' : ''}`}
            title={databaseName || 'Administration'}
          >
            <Database size={12} />
            <span>{badgeLabel}</span>
          </span>
          {appVersion && <span className={`stui-footer-version ${isPreprod ? 'stui-footer-version-preprod' : ''}`}><ScrollText size={12} />{appVersion}</span>}
          {status && <span className="stui-footer-status">{status}</span>}
        </div>
        <div className="stui-footer-center">
          {trial && (() => {
            // Apply writes where the theme picker's Apply to pointed when the trial started.
            const currentDbId = slugToDbId[activeDbSlug()];
            const scopeName = typeof trial.scope !== 'number' ? 'all databases'
              : trial.scope === currentDbId ? (dbDisplayName || databaseName || 'this database')
                : 'the database picked in Preferences';
            const apply = async () => {
              setApplyingTrial(true);
              try { await applyTrial(); } catch { notify.error(`Could not apply ${trial.label}. Check the connection and try again.`); } finally { setApplyingTrial(false); }
            };
            return (
              <div className="stui-footer-trial">
                <Eye size={12} />
                <span>Trying {trial.label}</span>
                <button type="button" className="stui-footer-trial-btn" title={`Keep ${trial.label} for ${scopeName}`} disabled={applyingTrial} onClick={apply}>Apply</button>
                <button type="button" className="stui-footer-trial-btn" title="Put your theme back" disabled={applyingTrial} onClick={stopTrial}>Stop</button>
              </div>
            );
          })()}
        </div>
        <div className="stui-footer-right">
          {/* Debug panel button — visible when any debug tool is active */}
          {debugActive && (
            <div className="stui-debug-wrap" ref={debugPanelRef}>
              {debugOpen && (
                <div className="stui-debug-panel">
                  <div className="stui-debug-head">
                    <span className="stui-debug-title">Debug Tools</span>
                    <span className="stui-debug-hint">Shift+F12</span>
                    <button className="stui-debug-close" onClick={() => setDebugOpen(false)}><X size={12} /></button>
                  </div>
                  <div className="stui-debug-section">Hover Inspectors</div>
                  {DEBUG_HOVER_TOOLS.map(t => {
                    const on = !!toolStates[t.lsKey];
                    return (
                      <React.Fragment key={t.key}>
                        <div className="stui-debug-row" onClick={() => handleDebugToggle(t.key, t.lsKey, 'hover', !on)}>
                          <span className="stui-debug-label">{t.label}</span>
                          <span className={`stui-debug-tog${on ? ' on' : ''}`} />
                        </div>
                        {t.key === 'cssVars' && (
                          <div className="stui-debug-row stui-debug-mode-row">
                            <span className="stui-debug-label stui-c-muted" style={{ fontSize: '10px' }}>Var mode:</span>
                            <div className="stui-flex-4">
                              <button className={`stui-debug-mode-btn${cssVarsMode === 'used' ? ' on' : ''}`} onClick={e => { e.stopPropagation(); handleCssVarsMode('used'); }}>Used</button>
                              <button className={`stui-debug-mode-btn${cssVarsMode === 'scope' ? ' on' : ''}`} onClick={e => { e.stopPropagation(); handleCssVarsMode('scope'); }}>In Scope</button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div className="stui-debug-section">Page Overlays</div>
                  {DEBUG_OVERLAY_TOOLS.map(t => {
                    const on = !!toolStates[t.lsKey];
                    return (
                      <div key={t.key} className="stui-debug-row" onClick={() => handleDebugToggle(t.key, t.lsKey, 'overlay', !on)}>
                        <span className="stui-debug-label">{t.label}</span>
                        <span className={`stui-debug-tog${on ? ' on' : ''}`} />
                      </div>
                    );
                  })}
                  <div className="stui-debug-section">Theme</div>
                  <div className="stui-debug-mode-row">
                    <StuiSelect className="stui-select" value={theme} onChange={e => handleFooterTheme(e.target.value)}>
                      {!themeChoices.some(t => t.themeKey === theme) && <option value={theme}>{theme}</option>}
                      {themeChoices.map(t => (
                        <option key={t.themeKey} value={t.themeKey}>{t.label}{t.status === 'Draft' ? ' (Draft)' : ''}</option>
                      ))}
                    </StuiSelect>
                  </div>
                </div>
              )}
              <button
                className={`stui-debug-btn${debugOpen ? ' active' : ''}`}
                onClick={() => setDebugOpen(o => !o)}
                title="Debug Tools (Shift+F12)"
              >
                <Wrench size={13} />
                {debugOpen ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
              </button>
            </div>
          )}
          {canReportBug && (
            <button
              className="stui-footer-add-task-btn"
              onClick={handleOpenModal}
              title={canQuickCapture ? 'Report a bug or capture a task' : 'Report a bug on this page'}
            >
              <Bug size={16} />
            </button>
          )}
          {/* Help button — every page; content resolves via HelpContext key */}
          <button
            className="stui-footer-help-btn"
            onClick={openHelp}
            title="How this page works"
          >
            <span className="stui-footer-help-qmark">?</span>
          </button>
        </div>
      </footer>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>{canQuickCapture ? 'Report a Bug or Capture a Task' : 'Report a Bug'}</h3>
              <button className="stui-modal-close" onClick={() => setShowAddTask(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-form-row-inline">
                <div className="stui-form-field">
                  <label>Source Page</label>
                  <input
                    type="text"
                    className="stui-field-input"
                    value={getSourcePage()}
                    readOnly
                    disabled
                  />
                </div>
                {/* Developers get the same control for ordinary quick capture; for
                    everyone else the field would only be noise. */}
                <div className="stui-form-field">
                  <label>Type</label>
                  {canQuickCapture ? (
                    <StuiSelect
                      className="stui-field-input"
                      value={newTask.entryType}
                      onChange={(e) => setNewTask({ ...newTask, entryType: e.target.value })}
                    >
                      {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </StuiSelect>
                  ) : (
                    <input type="text" className="stui-field-input" value="Bug" readOnly disabled />
                  )}
                </div>
                <div className="stui-form-field">
                  <label>Priority</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={newTask.category}
                    onChange={(e) => setNewTask({ ...newTask, category: e.target.value })}
                  >
                    {priorityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </StuiSelect>
                </div>
              </div>
              <div className="stui-form-row">
                <label>What went wrong?</label>
                <input
                  type="text"
                  className="stui-field-input"
                  value={newTask.task}
                  onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                  placeholder="One line - what you saw, on which screen"
                  autoFocus
                />
              </div>
              <div className="stui-form-row">
                <label>Details</label>
                <textarea
                  className="stui-field-input stui-field-textarea"
                  value={newTask.details}
                  onChange={(e) => setNewTask({ ...newTask, details: e.target.value })}
                  placeholder="What you were doing, what you expected, what happened instead..."
                  rows={5}
                />
              </div>
              <p className="stui-label-note">
                The page, build, database and browser you are on are attached automatically.
              </p>
            </div>
            <div className="stui-modal-footer">
              <button
                className="stui-btn stui-btn-secondary"
                onClick={() => setShowAddTask(false)}
              >
                Cancel
              </button>
              <button
                className="stui-btn stui-btn-primary"
                onClick={handleAddTask}
                disabled={saving || !newTask.task.trim()}
              >
                {saving ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal — view (everyone) / edit (action.edithelp) */}
      {showHelp && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>{helpEditing ? `Edit Help — ${helpKey}` : (helpRow?.title || 'Help')}</h3>
              <div className="stui-row-4">
                {!helpEditing && !helpLoading && (
                  <button className="stui-modal-close" onClick={() => setShowHelpEmail(true)} title="Email support about this page">
                    <Mail size={16} />
                  </button>
                )}
                {!helpEditing && canEditHelp && !helpLoading && (
                  <button className="stui-modal-close" onClick={startHelpEdit} title="Edit this page's help">
                    <Pencil size={16} />
                  </button>
                )}
                <button className="stui-modal-close" onClick={() => setShowHelp(false)}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {helpEditing ? (
              <>
                <div className="stui-modal-body">
                  <div className="stui-field-group stui-mb-md">
                    <label>Title</label>
                    <input
                      type="text"
                      className="stui-field-input"
                      value={helpDraft.title}
                      onChange={e => setHelpDraft(d => ({ ...d, title: e.target.value }))}
                    />
                  </div>
                  <StuiRichText
                    value={helpDraft.contentHtml}
                    onChange={html => setHelpDraft(d => ({ ...d, contentHtml: html }))}
                    placeholder="Write this page's help — what it does, the buttons, the gotchas..."
                    minHeight={260}
                  />
                </div>
                <div className="stui-modal-footer">
                  <button className="stui-btn stui-btn-secondary" onClick={() => setHelpEditing(false)}>Cancel</button>
                  <button
                    className="stui-btn stui-btn-primary"
                    onClick={saveHelp}
                    disabled={helpSaving || !helpDraft.title.trim()}
                  >
                    {helpSaving ? 'Saving...' : 'Save Help'}
                  </button>
                </div>
              </>
            ) : (
              <div className="stui-modal-body">
                {helpLoading ? (
                  <p className="stui-label-note">Loading…</p>
                ) : helpRow?.contentHtml ? (
                  <StuiRichTextViewer html={helpRow.contentHtml} />
                ) : (
                  <p className="stui-label-note">
                    No help has been written for this page yet.
                    {canEditHelp && ' Hit the pencil to write it.'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showHelpEmail && (
        <StuiEmailModal
          title="Email Support"
          audience="support"
          audienceLabel="DAWN Support"
          subjectQualifier={dbDisplayName}
          subject={helpEmailSubject}
          context={helpEmailContext}
          placeholder="What's the question, or what went wrong?"
          onClose={() => setShowHelpEmail(false)}
        />
      )}
    </>
  );
};

export default Footer;
